'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import type { MouseEventHandler } from 'react';
import { Button } from 'react-aria-components';
import { Image } from 'react-datocms';
import { Stack } from '~/components/Layout/Stack';
import { Link } from '~/components/Link';
import { NewsletterForm } from '~/components/marketing/NewsletterForm';
import { useSurvey } from '~/components/Survey/SurveyProvider';
import { isSeededMapEntryHref, resolveMapEntryHref } from '~/constants/maps';
import { socialMediaIcons } from '~/constants/socialIcons';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import type { FooterData } from '~/services/dato/navigation.service';
import type { MenuItem } from '~/types/Menu.types';
import { gtag } from '~/utils';
import { getCookie, setCookie } from '~/utils/cookies';
import { useSharedRouter } from '~/utils/nextMigration/router';
import styles from './Footer.module.css';
const year = new Date().getFullYear();
const Footer = t0 => {
  const $ = _c(49);
  const {
    footer
  } = t0;
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const newsletterSignup = api.automation.newsletterSignup.useMutation();
  let t1;
  if ($[0] !== locale || $[1] !== newsletterSignup) {
    t1 = (email, sourcePage) => newsletterSignup.mutateAsync({
      emailAddress: email,
      sourcePage,
      locale: locale as Locale
    });
    $[0] = locale;
    $[1] = newsletterSignup;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onSubscribe = t1;
  let t2;
  if ($[3] !== pathname) {
    t2 = pathname?.includes("/checkout");
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isCheckout = t2;
  if (pathname?.includes("/map")) {
    return null;
  }
  let t3;
  if ($[5] !== footer?.items) {
    t3 = footer?.items ?? [];
    $[5] = footer?.items;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const footerLinks = t3;
  let t4;
  if ($[7] !== footer?.socialLinks) {
    t4 = footer?.socialLinks ?? [];
    $[7] = footer?.socialLinks;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const socialMediaLinks = t4;
  let t5;
  if ($[9] !== footer?.certificationLogos) {
    t5 = footer?.certificationLogos ?? [];
    $[9] = footer?.certificationLogos;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const certificationLogos = t5;
  let t6;
  if ($[11] !== locale) {
    t6 = item => {
      const href = resolveMapEntryHref(item.url, locale);
      return {
        href,
        isExternal: isSeededMapEntryHref(href) ? false : item.isExternal
      };
    };
    $[11] = locale;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const resolveLeafHref = t6;
  let t7;
  if ($[13] !== footerLinks || $[14] !== resolveLeafHref) {
    let t8;
    if ($[16] !== resolveLeafHref) {
      t8 = footerLink => {
        if (!footerLink.items) {
          const link = resolveLeafHref(footerLink);
          return <Stack space="--by-space-400" key={footerLink.id}><ul><li><LinkText href={link.href} isExternal={link.isExternal} target="_self">{footerLink.title}</LinkText></li></ul></Stack>;
        }
        return <Stack space="--by-space-400" key={footerLink.id}>{footerLink.title && <h2 id={footerLink.title} className={styles.listTitle}>{footerLink.title}</h2>}<Stack space="--by-space-200"><ul aria-labelledby={footerLink.title || undefined}>{footerLink.items.map(item_0 => {
                const link_0 = resolveLeafHref(item_0);
                return <li key={item_0.id}><LinkText href={link_0.href} isExternal={link_0.isExternal} target="_self">{item_0.title}</LinkText></li>;
              })}</ul></Stack></Stack>;
      };
      $[16] = resolveLeafHref;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t7 = footerLinks.map(t8);
    $[13] = footerLinks;
    $[14] = resolveLeafHref;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[18] !== t7) {
    t8 = <div className={styles.pageLinks}>{t7}</div>;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== isCheckout || $[21] !== onSubscribe || $[22] !== t) {
    t9 = !isCheckout && <div className={styles.sidebar}><div><h2 className={styles.newsletterHeader}>{t("content.newsletter_header")}</h2><NewsletterForm source="footer" onSubscribe={onSubscribe} /></div></div>;
    $[20] = isCheckout;
    $[21] = onSubscribe;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== certificationLogos) {
    t10 = certificationLogos.map(_temp);
    $[24] = certificationLogos;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <div className={styles.logos}>{t10}</div>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== socialMediaLinks) {
    t12 = socialMediaLinks.length ? <Stack variant="horizontal" space="--by-space-400">{socialMediaLinks.map(_temp2)}</Stack> : null;
    $[28] = socialMediaLinks;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = t("common.cookie_preferences");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <Button data-test-id="cookie-preferences" className={styles.cookiePreferences} onPress={_temp3}>{t13}</Button>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t("common.copyright", {
      year
    });
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = <p className={styles.copyright}>{t15}</p>;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t14 || $[39] !== t16) {
    t17 = <div className={styles.footnoteContent}>{t14}{t16}</div>;
    $[38] = t14;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t11 || $[42] !== t12 || $[43] !== t17) {
    t18 = <div className={styles.footnote}>{t11}{t12}{t17}</div>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t18 || $[46] !== t8 || $[47] !== t9) {
    t19 = <footer className="px-3 pt-16 pb-40 text-sm bg-neutral-100 md:text-base print:hidden"><div className="container mx-auto md:px-8"><div className={styles.container}>{t8}{t9}{t18}</div></div></footer>;
    $[45] = t18;
    $[46] = t8;
    $[47] = t9;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
};
const LinkText = t0 => {
  const $ = _c(12);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const {
    setIsOpen
  } = useSurvey("general");
  const router = useSharedRouter();
  let t1;
  if ($[3] !== router.pathname) {
    t1 = router.pathname.includes("checkout/details");
    $[3] = router.pathname;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const isOnCheckoutDetailsPage = t1;
  let t2;
  if ($[5] !== isOnCheckoutDetailsPage || $[6] !== setIsOpen) {
    t2 = e => {
      if (!isOnCheckoutDetailsPage) {
        return;
      }
      if (getCookie("hasHadGeneralSurvey")) {
        return;
      }
      e.preventDefault();
      setIsOpen(true);
      gtag({
        event: "general_survey_triggered"
      });
      setCookie("hasHadGeneralSurvey", true);
    };
    $[5] = isOnCheckoutDetailsPage;
    $[6] = setIsOpen;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const surveyHandler = t2;
  let t3;
  if ($[8] !== children || $[9] !== props || $[10] !== surveyHandler) {
    t3 = <Link {...props} onClick={surveyHandler} href={props.href}>{children}</Link>;
    $[8] = children;
    $[9] = props;
    $[10] = surveyHandler;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export { Footer };
function _temp(certificationLogo, idx) {
  if (!certificationLogo.responsiveImage) {
    return null;
  }
  const isBCorp = /b[-\s]?corp/i.test(certificationLogo.alt ?? certificationLogo.responsiveImage.alt ?? "");
  const logo = <Image data={certificationLogo.responsiveImage} className={styles.logo} />;
  return <div key={certificationLogo.id ?? idx}>{isBCorp ? <Link href="https://bcorp.com/" isExternal={true}>{logo}</Link> : logo}</div>;
}
function _temp2(socialMediaLink) {
  const Icon = socialMediaIcons[socialMediaLink.platform];
  return <Link key={socialMediaLink.id} href={socialMediaLink.url} isExternal={true}><Icon ariaLabel={socialMediaLink.platform} title={socialMediaLink.platform} size="large" /></Link>;
}
function _temp3() {
  (globalThis as typeof globalThis & {
    Cookiebot?: {
      renew: () => void;
    };
  }).Cookiebot?.renew();
}